import React, { useState } from "react";
import { ChevronDown, ChevronRight, ScrollText } from "lucide-react";
import { useGetCallScriptsQuery } from "@/store/rest_api";
import type { CallScriptContext } from "@/model/interface/Call";
import { resolveScript } from "@/util/scriptTokens";
import { cn } from "@/lib/utils";

const COLLAPSED_STORAGE_KEY = "powerDialer.scriptCollapsed";

function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function saveCollapsedPreference(isCollapsed: boolean) {
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(isCollapsed));
  } catch {
    return;
  }
}

interface CallScriptPaneProps {
  scriptId?: string | null;
  context: CallScriptContext;
}

export function CallScriptPane({ scriptId, context }: CallScriptPaneProps) {
  const { data: scripts = [] } = useGetCallScriptsQuery({}, { skip: !scriptId });
  const [isCollapsed, setIsCollapsed] = useState(readCollapsedPreference);
  const script = scripts.find((candidate) => candidate.id === scriptId);

  const handleToggle = () => {
    const nextCollapsed = !isCollapsed;
    setIsCollapsed(nextCollapsed);
    saveCollapsedPreference(nextCollapsed);
  };

  if (!script) {
    return null;
  }

  const segments = resolveScript(script.body, context);

  return (
    <div className="shrink-0 rounded-lg border border-border">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold hover:bg-muted/50"
        onClick={handleToggle}
      >
        <ScrollText className="h-4 w-4 text-muted-foreground" />
        <span className="truncate">Script · {script.name}</span>
        <span className="ml-auto flex items-center gap-1 text-muted-foreground">
          {isCollapsed ? "Show" : "Hide"}
          {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {!isCollapsed && (
        <div className="max-h-64 overflow-y-auto whitespace-pre-wrap border-t border-border px-4 py-3 text-base leading-relaxed">
          {segments.map((segment, index) =>
            segment.isToken ? (
              <span
                key={index}
                className={cn(
                  "rounded px-1",
                  segment.isResolved ? "bg-success/15 font-medium text-success" : "bg-muted text-muted-foreground",
                )}
              >
                {segment.text}
              </span>
            ) : (
              <React.Fragment key={index}>{segment.text}</React.Fragment>
            ),
          )}
        </div>
      )}
    </div>
  );
}
